import { CanvasTexture, SRGBColorSpace } from 'three';

/**
 * 주행 중 차량 아래에 깔 접지 그림자 텍스처.
 *
 * 왜 ContactShadows를 안 쓰는가:
 *   드리의 ContactShadows는 씬 깊이를 텍스처로 구워서 실루엣이 정확합니다. 그런데
 *   `frames={1}`로 한 번만 굽기 때문에 움직이는 차를 따라갈 수 없고, 매 프레임 다시
 *   구우면(`frames={Infinity}`) 삼각형 35만 개짜리 차량을 프레임마다 한 번 더 렌더하게
 *   됩니다. 주행은 프레임 드랍이 바로 손에 느껴지는 모드라 그 비용을 낼 수 없습니다.
 *
 *   대신 타원형 그라디언트 판을 차량과 함께 움직입니다. 실루엣은 뭉뚱그려지지만,
 *   주행 시점은 카메라가 37m 떨어져 있어서(차가 프레임 폭의 19%) 실루엣의 정확도가
 *   보이지 않습니다. 보이는 것은 "차가 바닥에 붙어 있는지"뿐이고 그건 이걸로 충분합니다.
 *
 * 판 크기는 CarBounds에서 만들므로 여기서는 정사각 텍스처만 만듭니다. 판이 직사각형이면
 * 원형 그라디언트가 늘어나 타원이 됩니다.
 */

const CANVAS_SIZE = 128;

/**
 * 중심 불투명도. 1로 두면 차 밑이 검게 뚫린 것처럼 보입니다.
 * 최종 농도는 배경 프리셋의 contactShadowOpacity가 다시 곱합니다.
 */
const CORE_ALPHA = 0.85;

/**
 * 그라디언트 정지점.
 *
 * 선형으로 흐리면 경계가 원으로 도드라져 스티커처럼 보입니다. 안쪽을 넓게 유지하고
 * 바깥에서 빠르게 떨어뜨리면 접지면 주변만 어두워지는 실제 그림자에 가까워집니다.
 */
const STOPS: Array<[number, number]> = [
  [0, 1],
  [0.45, 0.85],
  [0.72, 0.35],
  [1, 0],
];

/**
 * @returns 2D 컨텍스트가 없는 환경(jsdom)에서는 null. 그림자 없이 렌더됩니다.
 */
export function createShadowBlobTexture(): CanvasTexture | null {
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_SIZE;
  canvas.height = CANVAS_SIZE;
  const context = canvas.getContext('2d');
  if (context === null) {
    return null;
  }

  const center = CANVAS_SIZE / 2;
  const gradient = context.createRadialGradient(center, center, 0, center, center, center);
  STOPS.forEach(([offset, alpha]) => {
    gradient.addColorStop(offset, `rgba(0, 0, 0, ${alpha * CORE_ALPHA})`);
  });

  context.fillStyle = gradient;
  context.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  return texture;
}
